import React, { useCallback, useMemo, useRef, useState } from 'react';
import { metricValueFormat } from '../../utils/metricValueFormat';
import smileFaceIcon from '../../assets/Smileface.png';
import sadFaceIcon from '../../assets/SadFace.png';
import mediumSmileIcon from '../../assets/Medium Smile.svg';
import PayoutCalculatorImage from '../../assets/PayoutCalculator.png';
import './metricAttainmentPayoutCalculator.scss';

interface MetricAttainmentPayoutCalculatorProps {
    data: any[];
    payoutRates: any[];
    resultData: any;
}

const LEVEL_COLOR: Record<number, string> = {
    1: '#E23636',
    2: '#1A5AA5',
    3: '#2AA154',
};

const BADGE_STYLE: Record<number, { bg: string; fg: string }> = {
    1: { bg: '#FCE9E8', fg: '#C23B34' },
    2: { bg: '#E3ECFB', fg: '#1A5AA5' },
    3: { bg: '#E6F5E8', fg: '#2F8A3E' },
};

const OMITTED_COLOR = '#B7BDC3';
const OMITTED_BADGE = { bg: '#E4E6E9', fg: '#5A6570' };

const MEETS_LEVEL_ID = 2;
const EXCEEDS_LEVEL_ID = 3;
const TOTAL_LEVELS = 3;

const MIN_HOURS_REQUIRED = 80;
const OT_MULTIPLIER = 1.5;

function fractionToLevel(fraction: number): number {
    const clamped = Math.min(Math.max(fraction, 0), 0.999);
    return Math.floor(clamped * TOTAL_LEVELS) + 1;
}

function rateForGlobalLevel(
    payoutRates: any[] | null | undefined,
    levelId: number
): number {
    return payoutRates?.find((pr) => pr.metric_attainment_level_id === levelId)?.payout_percentage ?? 0;
}

function formatValueBadge(row: any): string {
    if (row.metric_omitted) return '--';
    if (row.metric_value === null) return '—';
    return metricValueFormat(row.metric_value, row.metric_type_name);
}

function calculateProjectedPayout(
    projectedHours: number,
    payRate: number,
    payoutPercentage: number,
    minHours: number
): number {
    if (projectedHours < minHours) return 0;

    const otHours = Math.max(projectedHours - minHours, 0);
    const regularHours = projectedHours - otHours;
    const eligibleBasePay = regularHours * payRate + otHours * payRate * OT_MULTIPLIER;

    return (payoutPercentage / 100) * eligibleBasePay;
}

function formatDollars(amount: number): string {
    return amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

type AttainmentTier = 'red' | 'blue' | 'green';

function getAttainmentTier(levels: number[]): AttainmentTier {
    if (levels.length === 0) return 'blue';
    if (levels.some((lvl) => lvl === 1)) return 'red';
    if (levels.every((lvl) => lvl === EXCEEDS_LEVEL_ID)) return 'green';
    return 'blue';
}

const TIER_ALT_TEXT: Record<AttainmentTier, string> = {
    red: 'Below target',
    blue: 'Meeting target',
    green: 'Exceeding target',
};

interface BannerContent {
    icon: string;
    alt: string;
    iconClassName: string;
    isEligible: boolean;
    accentClassName: string;
    messagePrefix: string;
    highlight?: string;
    messageSuffix?: string;
}

const BANNER_CONTENT: Record<AttainmentTier, BannerContent> = {
    red: {
        icon: sadFaceIcon,
        alt: TIER_ALT_TEXT.red,
        iconClassName: 'map-card__eligibility-icon map-card__eligibility-icon--negative',
        isEligible: false,
        accentClassName: 'map-card__eligibility-text--negative',
        messagePrefix: 'Review your information to achieve payout.',
    },
    blue: {
        icon: mediumSmileIcon,
        alt: TIER_ALT_TEXT.blue,
        iconClassName: 'map-card__eligibility-icon',
        isEligible: true,
        accentClassName: 'map-card__eligibility-text--blue',
        messagePrefix: 'Review your information to ',
        highlight: 'improve',
        messageSuffix: ' your payout.',
    },
    green: {
        icon: smileFaceIcon,
        alt: TIER_ALT_TEXT.green,
        iconClassName: 'map-card__eligibility-icon',
        isEligible: true,
        accentClassName: 'map-card__eligibility-text--positive',
        messagePrefix: 'Great job, you have reached ',
        highlight: 'Maximum Payout',
        messageSuffix: '.',
    },
};

interface DisplayMetric {
    metricId: number;
    metricName: string;
    valueBadge: string;
    actualLevel: number;
    actualRate: number;
    meetsRate: number;
    exceedsRate: number;
    isPayoutMetric: boolean;
    isOmitted: boolean;
}

function rateForLevel(level: number, metric: DisplayMetric): number {
    switch (level) {
        case 3:
            return metric.exceedsRate;
        case 2:
            return metric.meetsRate;
        case 1:
        default:
            return 0;
    }
}

interface MetricAttainmentRowProps {
    metric: DisplayMetric;
    previewLevel: number;
    onPreviewLevelChange: (metricId: number, level: number) => void;
}

const MetricAttainmentRow: React.FC<MetricAttainmentRowProps> = ({
    metric,
    previewLevel,
    onPreviewLevelChange,
}) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const draggingRef = useRef(false);
    const interactive = metric.isPayoutMetric && !metric.isOmitted;

    const updateFromClientX = useCallback(
        (clientX: number) => {
            const track = trackRef.current;
            if (!track) return;
            const rect = track.getBoundingClientRect();
            const fraction = (clientX - rect.left) / rect.width;
            onPreviewLevelChange(metric.metricId, fractionToLevel(fraction));
        },
        [metric.metricId, onPreviewLevelChange]
    );

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!interactive) return;
        draggingRef.current = true;
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        updateFromClientX(e.clientX);
    };
    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!draggingRef.current || !interactive) return;
        updateFromClientX(e.clientX);
    };
    const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        draggingRef.current = false;
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    };

    const color = metric.isOmitted ? OMITTED_COLOR : LEVEL_COLOR[previewLevel] ?? '#9AA5AB';
    const badge = metric.isOmitted ? OMITTED_BADGE : BADGE_STYLE[metric.actualLevel] ?? BADGE_STYLE[1];
    const markerPosition = metric.isOmitted ? 0 : (previewLevel / TOTAL_LEVELS) * 100;
    const fillWidth = metric.isOmitted ? 100 : markerPosition;
    const displayedRate = rateForLevel(previewLevel, metric);

    const actualColor = metric.isOmitted ? OMITTED_COLOR : LEVEL_COLOR[metric.actualLevel] ?? '#9AA5AB';
    const actualDisplayedRate = rateForLevel(metric.actualLevel, metric);

    return (
        <div className="map-metric__row">
            <div className="map-metric__row-header">
                <span className="map-metric__row-name">{metric.metricName}</span>
                <span
                    className="map-metric__row-badge"
                    style={{ background: badge.bg, color: badge.fg }}
                >
                    {metric.valueBadge}
                </span>
            </div>

            <div className="map-metric__row-track-line">
                <div className="map-metric__slider-wrap">
                    <div
                        className={`map-metric__slider-track ${!interactive ? 'is-disabled' : ''}`}
                        ref={trackRef}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                    >
                        <div
                            className="map-metric__slider-fill"
                            style={{ width: `${fillWidth}%`, background: color }}
                        />
                        <div
                            className="map-metric__slider-handle"
                            style={{
                                left: `${markerPosition}%`,
                                borderTopColor: color,
                                transform: metric.isOmitted ? 'translateX(0)' : undefined,
                            }}
                        />
                    </div>
                    <span className="map-metric__row-caption" style={{ color: actualColor }}>
                        {metric.isOmitted ? 'Omitted Metric' : `Current: ${actualDisplayedRate.toFixed(2)}%`}
                    </span>
                </div>

                <span className="map-metric__row-rate" style={{ color }}>
                    {metric.isOmitted ? '--' : `${displayedRate.toFixed(2)}%`}
                </span>
            </div>
        </div>
    );
};

export const MetricAttainmentPayoutCalculator: React.FC<MetricAttainmentPayoutCalculatorProps> = ({
    data,
    payoutRates,
    resultData,
}) => {
    /* ---- Metric Attainment (left card) ---- */
    const { metrics, actualPayoutPercentage, actualEligible, targetedCount } = useMemo(() => {
        const rows = data ?? [];

        const meetsRateGlobal = rateForGlobalLevel(payoutRates, MEETS_LEVEL_ID);
        const exceedsRateGlobal = rateForGlobalLevel(payoutRates, EXCEEDS_LEVEL_ID);

        const displayMetrics: DisplayMetric[] = rows.map((row) => ({
            metricId: row.metric_id,
            metricName: row.label,
            valueBadge: formatValueBadge(row),
            actualLevel: row.metric_attainment_level_id ?? 1,
            actualRate: row.metric_payout_percentage ?? 0,
            meetsRate: meetsRateGlobal,
            exceedsRate: exceedsRateGlobal,
            isPayoutMetric: row.is_payout_metric,
            isOmitted: row.metric_omitted,
        }));

        const blendMembers = rows.filter((r) => r.is_payout_metric);
        const first = rows[0];

        const targetedMetricCount =
            first?.total_metrics_targeted ?? rows.filter((r) => r.is_payout_metric).length;

        const actualEligibleFromLevels = !blendMembers.some(
            (r) => (r.metric_attainment_level_id ?? 1) === 1
        );

        return {
            metrics: displayMetrics,
            actualPayoutPercentage: first?.payout_percentage ?? 0,
            actualEligible: actualEligibleFromLevels,
            targetedCount: targetedMetricCount,
        };
    }, [data, payoutRates]);

    const [previewLevels, setPreviewLevels] = useState<Record<number, number>>({});

    const handlePreviewLevelChange = useCallback((metricId: number, level: number) => {
        setPreviewLevels((prev) => ({ ...prev, [metricId]: level }));
    }, []);

    const isPreviewing = Object.keys(previewLevels).length > 0;

    const { blendedPercentage, blendedEligible } = useMemo(() => {
        const blendMembers = metrics.filter((m) => m.isPayoutMetric);
        if (blendMembers.length === 0) {
            return { blendedPercentage: 0, blendedEligible: true };
        }

        const levels = blendMembers.map((m) => previewLevels[m.metricId] ?? m.actualLevel);
        const anyBelowMeets = levels.some((lvl) => lvl === 1);

        if (anyBelowMeets) {
            return { blendedPercentage: 0, blendedEligible: false };
        }

        const rates = blendMembers.map((m, i) => rateForLevel(levels[i], m));
        const avg = rates.reduce((sum, r) => sum + r, 0) / rates.length;
        return { blendedPercentage: avg, blendedEligible: true };
    }, [metrics, previewLevels]);

    const displayedPercentage = isPreviewing ? blendedPercentage : actualPayoutPercentage;
    const metricsEligible = isPreviewing ? blendedEligible : actualEligible;

    const attainmentTier = useMemo(() => {
        const blendMembers = metrics.filter((m) => m.isPayoutMetric);
        const levels = blendMembers.map((m) =>
            isPreviewing ? previewLevels[m.metricId] ?? m.actualLevel : m.actualLevel
        );
        return getAttainmentTier(levels);
    }, [metrics, previewLevels, isPreviewing]);

    const actualTier = useMemo(() => {
        const blendMembers = metrics.filter((m) => m.isPayoutMetric);
        const levels = blendMembers.map((m) => m.actualLevel);
        return getAttainmentTier(levels);
    }, [metrics]);

    /* ---- Payout Calculator (right card) ---- */
    const payRate = resultData?.payrate ?? resultData?.pay_rate ?? 0;
    const actualPayout = resultData?.payout ?? 0;

    const [hoursInput, setHoursInput] = useState<string>('');
    const [calculatedHours, setCalculatedHours] = useState<number | null>(null);

    const handleCalculate = () => {
        const parsed = parseFloat(hoursInput);
        if (Number.isNaN(parsed) || parsed < 0) return;
        setCalculatedHours(parsed);
    };

    const hasCalculatedHours = calculatedHours !== null;
    const hoursEligible = hasCalculatedHours && calculatedHours >= MIN_HOURS_REQUIRED;

    // Recalculates whenever displayedPercentage changes (slider moves) or
    // calculatedHours changes (Calculate clicked) — this IS the shared state.
    const projectedPayout = hasCalculatedHours
        ? calculateProjectedPayout(calculatedHours, payRate, displayedPercentage, MIN_HOURS_REQUIRED)
        : 0;

    const bannerTier: AttainmentTier = !hoursEligible ? 'red' : attainmentTier;

    return (
        <div className="map-grid">
            {/* LEFT CARD — Metric Attainment */}
            <div className="map-card map-metric">
                <div className="map-card__header">
                    <h4 className="map-card__title">
                        Metric Attainment
                        <span className="map-card__info-wrap">
                            <span className="map-card__info-icon">
                                <i className="pi pi-question" />
                            </span>
                            <span className="map-card__tooltip" role="tooltip">
                                Slide the arrow to simulate payout at different targets
                            </span>
                        </span>
                    </h4>
                    <p className="map-card__disclaimer">
                        This data is only an estimate, please refer to your team lead for information
                    </p>
                </div>

                <div className="map-card__body">
                    <div className="map-metric__rows">
                        {metrics.map((metric) => (
                            <MetricAttainmentRow
                                key={metric.metricId}
                                metric={metric}
                                previewLevel={previewLevels[metric.metricId] ?? metric.actualLevel}
                                onPreviewLevelChange={handlePreviewLevelChange}
                            />
                        ))}
                    </div>

                    <div className="map-card__divider" />

                    <div className="map-card__summary">
                        <p className="map-card__summary-title">Payout Percentage</p>
                        <p
                            className={`map-card__summary-value ${BANNER_CONTENT[attainmentTier].accentClassName}`}
                        >
                            {displayedPercentage.toFixed(2)}%
                        </p>
                        <div className="map-card__summary-divider" />
                        {!metricsEligible ? (
                            <p className="map-card__summary-note">
                                To qualify for a payout, make sure you{' '}
                                <strong className="map-card__summary-note-highlight">
                                    Meet all {targetedCount} targets.
                                </strong>
                            </p>
                        ) : (
                            <p className="map-card__summary-note">
                                <strong>All targets met</strong> — you qualify for a payout.
                            </p>
                        )}
                        {isPreviewing && (
                            <p
                                className={`map-card__summary-current ${BANNER_CONTENT[actualTier].accentClassName}`}
                            >
                                Current: {actualPayoutPercentage.toFixed(2)}%
                            </p>
                        )}
                        {isPreviewing && (
                            <button
                                type="button"
                                className="p-button p-component btn-primary map-card__reset"
                                onClick={() => setPreviewLevels({})}
                            >
                                Reset
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* RIGHT CARD — Payout Calculator */}
            <div className="map-card map-calc">
                <div className="map-card__header">
                    <h4 className="map-card__title">
                        Payout Calculator
                        <span className="map-card__info-wrap">
                            <span className="map-card__info-icon">
                                <i className="pi pi-question" />
                            </span>
                            <span className="map-card__tooltip" role="tooltip">
                                Input your projected hours to simulate payout
                            </span>
                        </span>
                    </h4>
                    <p className="map-card__disclaimer">
                        This data is only an estimate, please refer to your team lead for information
                    </p>
                </div>

                <div className="map-card__body">
                    <div className="map-calc__input-col">
                        <label className="map-calc__label" htmlFor="projected-hours">
                            Projected Hours
                        </label>
                        <div className="map-calc__input-row">
                            <input
                                id="projected-hours"
                                type="number"
                                min={0}
                                className="map-calc__input"
                                value={hoursInput}
                                onChange={(e) => setHoursInput(e.target.value)}
                                placeholder="0"
                            />
                            <button
                                type="button"
                                className="map-calc__calculate-btn p-button p-component btn-primary"
                                onClick={handleCalculate}
                            >
                                Calculate
                            </button>
                        </div>
                        {hasCalculatedHours && !hoursEligible && (
                            <p className="map-calc__gate-message">
                                You must work at least {MIN_HOURS_REQUIRED} hours to qualify for a payout.
                            </p>
                        )}

                        <div className="map-calc__illustration">
                            <img src={PayoutCalculatorImage} alt="" />
                        </div>
                    </div>

                    <div className="map-card__divider" />

                    <div className="map-card__summary">
                        <p className="map-card__summary-title">Projected Payout</p>
                        <p
                            className={`map-card__summary-value ${
                                hasCalculatedHours ? BANNER_CONTENT[bannerTier].accentClassName : ''
                            }`}
                        >
                            {hasCalculatedHours ? formatDollars(projectedPayout) : '0'}
                        </p>
                        <div className="map-card__summary-divider" />
                        {hasCalculatedHours && !hoursEligible ? (
                            <p className="map-card__summary-note">
                                To qualify for a Payout, make sure you{' '}
                                <strong className="map-card__summary-note-highlight">
                                    meet all {MIN_HOURS_REQUIRED} hours
                                </strong>
                            </p>
                        ) : (
                            <p className="map-card__summary-note">
                                <strong>Hours requirement met</strong> — you may qualify for a payout.
                            </p>
                        )}
                        {isPreviewing && (
                            <p
                                className={`map-card__summary-current ${BANNER_CONTENT[actualTier].accentClassName}`}
                            >
                                Current: {formatDollars(actualPayout)}
                            </p>
                        )}
                    </div>
                </div>

                {hasCalculatedHours && (
                    <>
                        <div className="map-card__section-divider" />
                        <div className="map-card__eligibility-banner">
                            <span className={BANNER_CONTENT[bannerTier].iconClassName}>
                                <img
                                    src={BANNER_CONTENT[bannerTier].icon}
                                    alt={BANNER_CONTENT[bannerTier].alt}
                                />
                            </span>
                            <p className="map-card__eligibility-text">
                                You are{' '}
                                <strong className={BANNER_CONTENT[bannerTier].accentClassName}>
                                    {BANNER_CONTENT[bannerTier].isEligible ? 'Eligible' : 'Not Eligible'}
                                </strong>{' '}
                                for payout{BANNER_CONTENT[bannerTier].isEligible ? '' : ' yet'}.
                                <br />
                                {BANNER_CONTENT[bannerTier].messagePrefix}
                                {BANNER_CONTENT[bannerTier].highlight && (
                                    <strong className={BANNER_CONTENT[bannerTier].accentClassName}>
                                        {BANNER_CONTENT[bannerTier].highlight}
                                    </strong>
                                )}
                                {BANNER_CONTENT[bannerTier].messageSuffix}
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MetricAttainmentPayoutCalculator;
